// ============================================================
// L6 · woResolver — multi-modal Work Order resolution
//
// Modes:
//   1. Direct ID         — "WO-2026-0042" or "0042" / "42"
//   2. By customer       — "WO de Acme"  → most recent WO for that customer
//   3. By product        — "WO panel solar 400W"
//   4. By line           — "orden en LINE-A" / "WO en línea A"
//   5. "actual" / "in progress" — single in-progress WO (or most recent)
//   6. "última terminada" / "last completed"
// ============================================================

import { mockWorkOrders } from '@/data/mockWorkOrders';
import { findCustomerByName } from '@/data/mockCustomers';
import { findProductByDescription } from '@/data/mockProducts';
import type { WorkOrder } from '@/types/workOrder';

export type ResolveMode = 'id' | 'customer' | 'product' | 'line' | 'current' | 'last_completed' | 'unresolved';

export interface ResolveResult {
  wo: WorkOrder | null;
  mode: ResolveMode;
  hint?: string;            // human-readable detail
  /**
   * L9.5 · The ID to use in URLs / UIActions. Equals the user-supplied catalog ID
   * when one was used (e.g. "WO-2024-001", "WO-005"); otherwise equals wo.id.
   * This keeps the public catalog/route surface stable even though mocks use a
   * different internal ID space (WO-2026-NNNN).
   */
  displayId?: string;
}

// L9.5 · Catalog ID alias map (WO-2024-NNN → mock WO-2026-NNNN)
// The official transaction catalog uses WO-2024-001..012; mocks use WO-2026-0042..0053.
// This map preserves the catalog as source of truth without renaming mock IDs.
const CATALOG_ID_ALIAS: Record<string, string> = {
  'WO-2024-001': 'WO-2026-0042',
  'WO-2024-002': 'WO-2026-0043',
  'WO-2024-003': 'WO-2026-0044',
  'WO-2024-004': 'WO-2026-0045',
  'WO-2024-005': 'WO-2026-0046',
  'WO-2024-006': 'WO-2026-0047',
  'WO-2024-007': 'WO-2026-0048',
  'WO-2024-008': 'WO-2026-0049',
  'WO-2024-009': 'WO-2026-0050',
  'WO-2024-010': 'WO-2026-0051',
  'WO-2024-011': 'WO-2026-0052',
  'WO-2024-012': 'WO-2026-0053',
};

// Reverse map: real mock id → catalog id (used as default displayId for stable routes)
const MOCK_TO_CATALOG: Record<string, string> = Object.fromEntries(
  Object.entries(CATALOG_ID_ALIAS).map(([cat, mock]) => [mock, cat])
);

/** Default display id for a WO: prefer the catalog alias, else the mock id. */
function defaultDisplayId(wo: WorkOrder): string {
  return MOCK_TO_CATALOG[wo.id] ?? wo.id;
}

// 1. Direct id (full or short numeric form, with catalog alias support)
function tryById(message: string): { wo: WorkOrder; displayId: string } | null {
  // L9.5 · accept "WO-YYYY-NNN", "WO YYYY-NNN", "WO YYYY NNN" (loose separators)
  const full = message.match(/WO[-\s]\d{4}[-\s]\d{3,4}/i);
  if (full) {
    const id = full[0].toUpperCase().replace(/\s+/g, '-');
    const resolved = CATALOG_ID_ALIAS[id] ?? id;
    const wo = mockWorkOrders.find((w) => w.id === resolved);
    return wo ? { wo, displayId: id } : null;
  }
  // L9.5 · Short catalog form WO-NNN (3 digits, no year) → catalog alias WO-2024-NNN
  const shortCatalog = message.match(/\bWO-(\d{3})\b/i);
  if (shortCatalog) {
    const padded = `WO-2024-${shortCatalog[1]}`;
    const resolved = CATALOG_ID_ALIAS[padded];
    if (resolved) {
      const wo = mockWorkOrders.find((w) => w.id === resolved);
      if (wo) return { wo, displayId: padded };
    }
  }
  // Short forms: "WO 42", "WO-42", "WO0042", "orden 42", "orden 0042", "order 5"
  const short = message.match(/(?:WO[-\s]?|orden(?:\s+de\s+trabajo)?\s+|order\s+|work\s+order\s+)0*(\d{1,4})\b/i);
  if (short) {
    const numRaw = short[1];
    // Try catalog short id first (1-3 digits → WO-2024-NNN)
    if (numRaw.length <= 3) {
      const catId = `WO-2024-${numRaw.padStart(3, '0')}`;
      const aliased = CATALOG_ID_ALIAS[catId];
      if (aliased) {
        const wo = mockWorkOrders.find((w) => w.id === aliased);
        if (wo) return { wo, displayId: catId };
      }
    }
    // Fallback: legacy mock-id short form (4 digits, e.g. "0042")
    const num = numRaw.padStart(4, '0');
    const wo = mockWorkOrders.find((w) => w.id.endsWith(`-${num}`));
    if (wo) return { wo, displayId: defaultDisplayId(wo) };
  }
  return null;
}

// 2. Customer — "WO de Acme", "Acme work order"
function tryByCustomer(message: string): WorkOrder | null {
  // Patterns we accept: "de <name>", "de cliente <name>", "<Name> work order", "<Name> wo"
  const patterns: RegExp[] = [
    /(?:WO|orden(?:\s+de\s+trabajo)?|order|work\s+order)\s+(?:de(?:l)?\s+(?:cliente\s+)?|del\s+cliente\s+|for\s+|from\s+)([A-Za-zÁÉÍÓÚñÑ][\w\s.&-]{1,30}?)(?:\s|$|\?|,|\.)/i,
    /\b([A-Za-zÁÉÍÓÚñÑ][\w&-]{2,15})\s+(?:work\s+order|wo)\b/i,
  ];
  for (const re of patterns) {
    const m = message.match(re);
    if (!m) continue;
    const customer = findCustomerByName(m[1].trim());
    if (!customer) continue;
    const candidates = mockWorkOrders
      .filter((w) => w.customerId === customer.id)
      .sort((a, b) => new Date(b.lastEventAt).getTime() - new Date(a.lastEventAt).getTime());
    if (candidates.length > 0) return candidates[0];
  }
  return null;
}

// 3. Product — "WO panel solar 400W", "WO 400W solar panel"
function tryByProduct(message: string): WorkOrder | null {
  const probe = ['panel solar 400w', 'panel solar 450w', 'inversor 5kw', '400w solar', '450w bifacial', 'cabina aluminio', 'módulo elevación', 'modulo elevacion', 'elevation module'];
  const lower = message.toLowerCase();
  for (const term of probe) {
    if (lower.includes(term)) {
      const product = findProductByDescription(term.replace('400w', '400W').replace('450w', '450W').replace('5kw', '5kW'));
      if (product) {
        const candidates = mockWorkOrders
          .filter((w) => w.productSkuId === product.skuId)
          .sort((a, b) => new Date(b.lastEventAt).getTime() - new Date(a.lastEventAt).getTime());
        if (candidates.length > 0) return candidates[0];
      }
    }
  }
  return null;
}

// 4. Line — "WO en LINE-A", "orden en línea A"
function tryByLine(message: string): WorkOrder | null {
  const m = message.match(/(?:LINE-([ABC])|l[ií]nea\s+([abcABC])|line\s+([abcABC]))\b/i);
  if (!m) return null;
  const letter = (m[1] || m[2] || m[3]).toUpperCase();
  const lineId = `LINE-${letter}`;
  const candidates = mockWorkOrders
    .filter((w) => w.lineId === lineId)
    .sort((a, b) => new Date(b.lastEventAt).getTime() - new Date(a.lastEventAt).getTime());
  return candidates[0] ?? null;
}

// 5. "actual" / "in-progress"
function tryCurrent(message: string): WorkOrder | null {
  if (!/(WO\s+actual|orden\s+actual|current\s+wo|wo\s+(?:in[-\s]?)?progress|en\s+curso|in[-\s]?progress\s+wo)/i.test(message)) return null;
  const inProgress = mockWorkOrders.filter((w) => w.status === 'in_progress');
  return inProgress.sort((a, b) => a.priority - b.priority || new Date(b.lastEventAt).getTime() - new Date(a.lastEventAt).getTime())[0] ?? null;
}

// 6. Last completed
function tryLastCompleted(message: string): WorkOrder | null {
  if (!/(?:[úu]ltima\s+(?:wo\s+)?(?:terminad|complet|cerrad)|last\s+(?:completed|finished|closed)\s+(?:wo|work\s+order))/i.test(message)) return null;
  const completed = mockWorkOrders.filter((w) => w.status === 'completed');
  return completed.sort((a, b) => new Date(b.actualEndAt ?? b.lastEventAt).getTime() - new Date(a.actualEndAt ?? a.lastEventAt).getTime())[0] ?? null;
}

export function resolveWorkOrder(message: string): ResolveResult {
  const byId = tryById(message);
  if (byId) return { wo: byId.wo, mode: 'id', hint: byId.displayId, displayId: byId.displayId };

  const byCurrent = tryCurrent(message);
  if (byCurrent) return { wo: byCurrent, mode: 'current', hint: 'WO en curso', displayId: defaultDisplayId(byCurrent) };

  const byLast = tryLastCompleted(message);
  if (byLast) return { wo: byLast, mode: 'last_completed', hint: 'última completada', displayId: defaultDisplayId(byLast) };

  const byCustomer = tryByCustomer(message);
  if (byCustomer) return { wo: byCustomer, mode: 'customer', hint: byCustomer.customerName, displayId: defaultDisplayId(byCustomer) };

  const byProduct = tryByProduct(message);
  if (byProduct) return { wo: byProduct, mode: 'product', hint: byProduct.productDescription, displayId: defaultDisplayId(byProduct) };

  const byLine = tryByLine(message);
  if (byLine) return { wo: byLine, mode: 'line', hint: byLine.lineId, displayId: defaultDisplayId(byLine) };

  return { wo: null, mode: 'unresolved' };
}
